import { useMemo, useState } from "react";
import {
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	FormControl,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import { format } from "date-fns";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";

const today = () => format(new Date(), "yyyy-MM-dd");

/** Filter dropdowns (align with BQ BRAND / CHANNEL values). */
const DISPATCH_MAILER_BRAND_OPTIONS = ["RARE ONES", "RARE RABBIT", "RAREISM", "RTPL"];
const DISPATCH_MAILER_CHANNEL_OPTIONS = ["EBO", "LFS", "MBO"];

/** Stable key: customer + brand + channel + reference_site (must match API). */
const rowKey = (p) =>
	JSON.stringify([
		p.customer_name,
		p.brand != null ? String(p.brand) : "",
		p.channel != null ? String(p.channel) : "",
		p.reference_site != null ? String(p.reference_site) : "",
	]);

const partnerSearchText = (p) =>
	[
		p.customer_name,
		p.reference_site,
		p.brand,
		p.channel,
		p.sheet_brand,
		p.short_name,
		p.partner_type,
		p.poc_name,
		p.asm_name,
		p.zsm_name,
		...(p.poc_emails || []),
		...(p.asm_emails || []),
		...(p.zsm_emails || []),
		p.has_mapping && (p.poc_emails || []).length > 0
			? "ready"
			: p.has_mapping
				? "no email"
				: "not in sheet",
	]
		.filter((v) => v != null && String(v).trim() !== "")
		.join(" ")
		.toLowerCase();

const DispatchMailer = () => {
	const [startDate, setStartDate] = useState(today());
	const [endDate, setEndDate] = useState(today());
	const [brandFilter, setBrandFilter] = useState("");
	const [channelFilter, setChannelFilter] = useState("");
	const [loading, setLoading] = useState(false);
	const [sending, setSending] = useState(false);
	const [partners, setPartners] = useState([]);
	const [selected, setSelected] = useState(new Set());
	const [runSummary, setRunSummary] = useState(null);
	const [error, setError] = useState("");
	const [bqBrandColumn, setBqBrandColumn] = useState(null);
	const [bqChannelColumn, setBqChannelColumn] = useState(null);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(25);
	const [searchQuery, setSearchQuery] = useState("");

	const filteredPartners = useMemo(() => {
		const q = searchQuery.trim().toLowerCase();
		if (!q) return partners;
		return partners.filter((p) => partnerSearchText(p).includes(q));
	}, [partners, searchQuery]);

	const selectableKeys = useMemo(
		() =>
			filteredPartners
				.filter((p) => p.has_mapping && (p.poc_emails || []).length > 0)
				.map((p) => rowKey(p)),
		[filteredPartners]
	);

	const allSelected = selectableKeys.length > 0 && selectableKeys.every((k) => selected.has(k));
	const someSelected = selectableKeys.some((k) => selected.has(k));

	const selectedCustomerCount = useMemo(() => {
		const names = new Set();
		partners.forEach((p) => {
			if (selected.has(rowKey(p))) names.add(p.customer_name);
		});
		return names.size;
	}, [partners, selected]);

	const isSearching = searchQuery.trim() !== "";

	const pagedPartners = useMemo(() => {
		if (isSearching) return filteredPartners;
		const start = page * rowsPerPage;
		return filteredPartners.slice(start, start + rowsPerPage);
	}, [filteredPartners, page, rowsPerPage, isSearching]);

	const toggleAll = () => {
		setSelected((prev) => {
			const next = new Set(prev);
			if (allSelected) {
				selectableKeys.forEach((k) => next.delete(k));
			} else {
				selectableKeys.forEach((k) => next.add(k));
			}
			return next;
		});
	};

	const toggleOne = (k) => {
		setSelected((prev) => {
			const next = new Set(prev);
			if (next.has(k)) next.delete(k);
			else next.add(k);
			return next;
		});
	};

	const loadPartners = async () => {
		setError("");
		setRunSummary(null);
		setLoading(true);
		try {
			const { data } = await pythonVmInstance.get("/dispatch_mailer/partners", {
				params: {
					start_date: startDate,
					end_date: endDate,
					...(brandFilter.trim() ? { brand: brandFilter.trim() } : {}),
					...(channelFilter.trim() ? { channel: channelFilter.trim() } : {}),
				},
			});
			const list = data?.partners || [];
			setBqBrandColumn(data?.bq_brand_column || null);
			setBqChannelColumn(data?.bq_channel_column || null);
			setPartners(list);
			setSearchQuery("");
			setPage(0);
			setSelected(
				new Set(
					list.filter((p) => p.has_mapping && (p.poc_emails || []).length > 0).map((p) => rowKey(p))
				)
			);
		} catch (err) {
			setError(err?.response?.data?.error || err.message || "Failed to load partners");
			setPartners([]);
			setSearchQuery("");
			setPage(0);
		} finally {
			setLoading(false);
		}
	};

	const triggerSend = async ({ dryRun }) => {
		if (selected.size === 0) {
			setError("Select at least one partner");
			return;
		}
		setError("");
		setSending(true);
		try {
			const selectedPartners = partners
				.filter((p) => selected.has(rowKey(p)))
				.map((p) => ({
					customer_name: p.customer_name,
					brand: p.brand != null ? String(p.brand) : "",
					channel: p.channel != null ? String(p.channel) : "",
					reference_site: p.reference_site != null ? String(p.reference_site) : "",
				}));
			const { data } = await pythonVmInstance.post("/dispatch_mailer/send", {
				start_date: startDate,
				end_date: endDate,
				partners: selectedPartners,
				dry_run: dryRun,
				include_cc: true,
			});
			setRunSummary({ ...data, _client_ts: new Date().toISOString() });
		} catch (err) {
			setError(err?.response?.data?.error || err.message || "Send failed");
		} finally {
			setSending(false);
		}
	};

	const statusChip = (status) => {
		const map = {
			sent: "success",
			dry_run: "info",
			skipped: "warning",
			error: "error",
		};
		return <Chip size="small" color={map[status] || "default"} label={status} />;
	};

	const emailLogColor = (status) => {
		if (status === "sent" || status === "dry_run") return "success";
		if (status === "error") return "error";
		if (status === "skipped") return "warning";
		return "default";
	};

	const formatRunTime = (isoLike) => {
		if (!isoLike) return "—";
		try {
			const d = new Date(isoLike);
			return d.toLocaleString("en-IN", { timeZone: "Asia/Kolkata" });
		} catch {
			return String(isoLike);
		}
	};

	return (
		<Box sx={{ p: 2 }}>
			<Typography variant="h6" sx={{ mb: 0.5 }}>
				Partner Dispatch Mailer
			</Typography>
			{bqBrandColumn || bqChannelColumn ? (
				<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
					{bqBrandColumn ? (
						<>
						</>
					) : null}
					{bqChannelColumn ? (
						<>
						</>
					) : null}
				</Typography>
			) : (
				<Box sx={{ mb: 2 }} />
			)}

			<Paper sx={{ p: 2, mb: 2 }}>
				<Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
					<TextField
						label="Start Date"
						type="date"
						size="small"
						value={startDate}
						onChange={(e) => setStartDate(e.target.value)}
						InputLabelProps={{ shrink: true }}
					/>
					<TextField
						label="End Date"
						type="date"
						size="small"
						value={endDate}
						onChange={(e) => setEndDate(e.target.value)}
						InputLabelProps={{ shrink: true }}
					/>
					<FormControl size="small" sx={{ minWidth: 200 }} variant="outlined">
						<InputLabel>Brand</InputLabel>
						<Select
							value={brandFilter}
							label="Brand"
							onChange={(e) => setBrandFilter(e.target.value)}>
							<MenuItem value="">
								<em>All</em>
							</MenuItem>
							{DISPATCH_MAILER_BRAND_OPTIONS.map((b) => (
								<MenuItem key={b} value={b}>
									{b}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<FormControl size="small" sx={{ minWidth: 200 }} variant="outlined">
						<InputLabel>Channel</InputLabel>
						<Select
							value={channelFilter}
							label="Channel"
							onChange={(e) => setChannelFilter(e.target.value)}>
							<MenuItem value="">
								<em>All</em>
							</MenuItem>
							{DISPATCH_MAILER_CHANNEL_OPTIONS.map((ch) => (
								<MenuItem key={ch} value={ch}>
									{ch}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<Button variant="contained" onClick={loadPartners} disabled={loading || sending}>
						{loading ? <CircularProgress size={18} /> : "Preview Partners"}
					</Button>
					<Button
						variant="contained"
						color="success"
						onClick={() => triggerSend({ dryRun: false })}
						disabled={sending || loading || selected.size === 0}>
						{sending ? (
							<CircularProgress size={18} />
						) : (
							`Send Emails (${selectedCustomerCount} customer${selectedCustomerCount === 1 ? "" : "s"})`
						)}
					</Button>
				</Stack>
				<Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1, mb: 0 }}>
					One email per customer, with selected stores combined in a single attachment.
				</Typography>
				{error ? (
					<Typography color="error" sx={{ mt: 2 }}>
						{error}
					</Typography>
				) : null}
			</Paper>

			{partners.length > 0 && (
				<TableContainer component={Paper} sx={{ mb: 2 }}>
					<Box sx={{ p: 2, pb: 1 }}>
						<TextField
							fullWidth
							size="small"
							label="Search all partners"
							placeholder="Customer name, reference site, brand, POC, email…"
							value={searchQuery}
							onChange={(e) => {
								setSearchQuery(e.target.value);
								setPage(0);
							}}
						/>
						{isSearching ? (
							<Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
								{filteredPartners.length} match{filteredPartners.length === 1 ? "" : "es"} across{" "}
								{partners.length} partners
							</Typography>
						) : null}
					</Box>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell padding="checkbox">
									<Checkbox
										checked={allSelected}
										indeterminate={someSelected && !allSelected}
										onChange={toggleAll}
									/>
								</TableCell>
								<TableCell>Customer Name</TableCell>
								<TableCell>Reference Site</TableCell>
								<TableCell>Brand</TableCell>
								<TableCell>Channel</TableCell>
								<TableCell>Partner's Brand (sheet)</TableCell>
								<TableCell>Short Name</TableCell>
								<TableCell>Partner Type</TableCell>
								<TableCell>POC</TableCell>
								<TableCell>POC Email</TableCell>
								<TableCell>ASM</TableCell>
								<TableCell>ASM Email</TableCell>
								<TableCell>ZSM</TableCell>
								<TableCell>ZSM Email</TableCell>
								<TableCell align="right">Rows</TableCell>
								<TableCell align="right">Qty</TableCell>
								<TableCell>Status</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{pagedPartners.length === 0 ? (
								<TableRow>
									<TableCell colSpan={17} align="center" sx={{ py: 3, color: "text.secondary" }}>
										No partners match “{searchQuery.trim()}”
									</TableCell>
								</TableRow>
							) : null}
							{pagedPartners.map((p) => {
								const k = rowKey(p);
								const mapped = p.has_mapping && (p.poc_emails || []).length > 0;
								return (
									<TableRow key={k} hover selected={selected.has(k)}>
										<TableCell padding="checkbox">
											<Checkbox
												disabled={!mapped}
												checked={selected.has(k)}
												onChange={() => toggleOne(k)}
											/>
										</TableCell>
										<TableCell>{p.customer_name}</TableCell>
										<TableCell>
											{p.reference_site != null && p.reference_site !== "" ? p.reference_site : "—"}
										</TableCell>
										<TableCell>{p.brand != null && p.brand !== "" ? p.brand : "—"}</TableCell>
										<TableCell>
											{p.channel != null && p.channel !== "" ? p.channel : "—"}
										</TableCell>
										<TableCell>{p.sheet_brand != null && p.sheet_brand !== "" ? p.sheet_brand : "—"}</TableCell>
										<TableCell>{p.short_name || "--"}</TableCell>
										<TableCell>{p.partner_type || "--"}</TableCell>
										<TableCell>{p.poc_name || "--"}</TableCell>
										<TableCell>{(p.poc_emails || []).join(", ") || "--"}</TableCell>
										<TableCell>{p.asm_name || "--"}</TableCell>
										<TableCell>{(p.asm_emails || []).join(", ") || "--"}</TableCell>
										<TableCell>{p.zsm_name || "--"}</TableCell>
										<TableCell>{(p.zsm_emails || []).join(", ") || "--"}</TableCell>
										<TableCell align="right">{p.rows}</TableCell>
										<TableCell align="right">{Math.round(p.total_qty || 0)}</TableCell>
										<TableCell>
											{mapped ? (
												<Chip size="small" color="success" label="Ready" />
											) : p.has_mapping ? (
												<Chip size="small" color="warning" label="No email" />
											) : (
												<Chip size="small" color="error" label="Not in sheet" />
											)}
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
					{isSearching ? null : (
						<TablePagination
							component="div"
							count={filteredPartners.length}
							page={page}
							onPageChange={(_, nextPage) => setPage(nextPage)}
							rowsPerPage={rowsPerPage}
							onRowsPerPageChange={(e) => {
								setRowsPerPage(parseInt(e.target.value, 10));
								setPage(0);
							}}
							rowsPerPageOptions={[25, 50, 100]}
						/>
					)}
				</TableContainer>
			)}

			{runSummary && (
				<Paper sx={{ p: 2 }}>
					<Typography variant="subtitle1" sx={{ mb: 1 }}>
						{runSummary.dry_run ? "Dry Run Result" : "Send Result"} — sent: {runSummary.summary.sent}, dry_run:{" "}
						{runSummary.summary.dry_run}, skipped: {runSummary.summary.skipped}, error: {runSummary.summary.error}
					</Typography>
					<Box sx={{ mb: 2 }}>
						<Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
							Email Send Log
						</Typography>
						<Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
							{(runSummary.results || []).map((r, i) => (
								<Box
									key={`log-${r.customer_name}-${r.brand}-${r.channel}-${r.reference_site}-${i}`}
									sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
									<Chip
										label={(r.status || "unknown").toUpperCase()}
										size="small"
										color={emailLogColor(r.status)}
										sx={{ minWidth: 100, fontSize: "0.7rem" }}
									/>
									<Box sx={{ flex: 1 }}>
										<Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
											{`${r.customer_name || "—"} | ${r.store_count || (r.reference_sites || []).length || "—"} store(s) | ${
												r.reference_site || "—"
											} — ${r.reason || r.filename || "Processed"}`}
										</Typography>
										<Typography variant="caption" color="text.disabled" display="block">
											{formatRunTime(r.created_at || runSummary._client_ts)}
										</Typography>
									</Box>
								</Box>
							))}
						</Box>
					</Box>
					<TableContainer>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>Customer</TableCell>
									<TableCell>Stores</TableCell>
									<TableCell>Reference Sites</TableCell>
									<TableCell>Brand</TableCell>
									<TableCell>Channel</TableCell>
									<TableCell>Status</TableCell>
									<TableCell>To</TableCell>
									<TableCell>Cc</TableCell>
									<TableCell align="right">Rows</TableCell>
									<TableCell>File / Reason</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{(runSummary.results || []).map((r, i) => (
									<TableRow key={`${r.customer_name}-${r.brand}-${r.channel}-${r.reference_site}-${i}`}>
										<TableCell>{r.customer_name}</TableCell>
										<TableCell align="right">
											{r.store_count ?? (r.reference_sites || []).length ?? "—"}
										</TableCell>
										<TableCell>
											{(r.reference_sites || []).length
												? r.reference_sites.join(", ")
												: r.reference_site != null && r.reference_site !== ""
													? r.reference_site
													: "—"}
										</TableCell>
										<TableCell>{r.brand != null && r.brand !== "" ? r.brand : "—"}</TableCell>
										<TableCell>{r.channel != null && r.channel !== "" ? r.channel : "—"}</TableCell>
										<TableCell>{statusChip(r.status)}</TableCell>
										<TableCell>{(r.to || []).join(", ")}</TableCell>
										<TableCell>{(r.cc || []).join(", ")}</TableCell>
										<TableCell align="right">{r.rows ?? "--"}</TableCell>
										<TableCell>{r.filename || r.reason || ""}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				</Paper>
			)}
		</Box>
	);
};

export default DispatchMailer;
